import { clearQuestionRecovery } from '../lib/questionRecovery';
import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyToken } from '../lib/jwtUtils';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { SESSION_ENDED, endSession } from '../lib/sessionFailure';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionError, setSessionError] = useState('');
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const expired = () => { setIsAuthenticated(false); setUser(null); setSessionError(''); navigate('/login', { replace: true }); };
    window.addEventListener(SESSION_ENDED, expired);
    return () => window.removeEventListener(SESSION_ENDED, expired);
  }, [navigate]);

  // Check for existing auth token on app load
  useEffect(() => {
    let active = true;
    const clearAuthState = () => {
      setIsAuthenticated(false);
      setUser(null);
    };
    const validateAndSetAuth = async () => {
      const token = localStorage.getItem('authToken');
      const currentPath = location.pathname;

      if (token) {
        try {
          // Verify token is valid and not expired
          const payload = verifyToken(token);

          if (payload) {

            // Fetch user profile to ensure user still exists and is active
            const response = await api.get('/auth/me');
            if (!active || localStorage.getItem('authToken') !== token) return;
            const userData = response.data;

            // Check if user has SUPER_ADMIN or content roles
            const roles = userData.roles || [];
            const isSuperAdmin = roles.includes('ROLE_SUPER_ADMIN') || roles.includes('SUPER_ADMIN');
            const isAnalyst = roles.includes('ROLE_ANALYST') || roles.includes('ANALYST');
            const isContentSupervisor = roles.includes('ROLE_CONTENT_SUPERVISOR') || roles.includes('CONTENT_SUPERVISOR');
            const isContentGenerator = roles.includes('ROLE_CONTENT_GENERATOR') || roles.includes('CONTENT_GENERATOR');
            const isContentReviewer = roles.includes('ROLE_CONTENT_REVIEWER') || roles.includes('CONTENT_REVIEWER');

            if (isSuperAdmin || isAnalyst || isContentSupervisor || isContentGenerator || isContentReviewer) {
              localStorage.removeItem('activeRole');
              setIsAuthenticated(true);
              setSessionError('');
              setUser({
                id: userData.id || payload.sub || payload.userId,
                name: userData.name || localStorage.getItem('userName'),
                email: userData.email,
                roles: roles,
                token: token,
                isSuperAdmin: isSuperAdmin
              });

              // If we're on the login page, redirect to dashboard
              if (currentPath === '/login') {
                navigate('/', { replace: true });
              }

              setIsLoading(false);
              return;
            } else {
              endSession(token, 'This account cannot access the Super-admin portal. Contact your administrator.');
              clearAuthState();
              // User doesn't have required role, clear auth
              localStorage.removeItem('authToken');
              localStorage.removeItem('userRole');
              localStorage.removeItem('userName');
              localStorage.removeItem('userEmail');
            }
          } else {
            endSession(token);
            clearAuthState();

            // Clear the invalid token
            localStorage.removeItem('authToken');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userName');
            localStorage.removeItem('userEmail');

            // If we're not on a public page, redirect to login
            const publicRoutes = ['/login', '/otp-verification'];
            if (!publicRoutes.includes(currentPath)) {
              navigate('/login', { replace: true });
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          if (!active || localStorage.getItem('authToken') !== token) return;
          if ([401, 403].includes(error.response?.status)) {
            endSession(token, error.response.status === 403 ? 'This account cannot access the Super-admin portal. Contact your administrator.' : undefined);
            localStorage.removeItem('authToken');
            localStorage.removeItem('userRole');
            localStorage.removeItem('userName');
            localStorage.removeItem('userEmail');
            clearAuthState();
          } else {
            setSessionError('Unable to verify your session. Your sign-in has been retained. Check your connection and retry.');
          }
        }
      } else {
        clearAuthState();
      }

      if (active) setIsLoading(false);
    };

    validateAndSetAuth();
    return () => { active = false; };
  }, [location.pathname, navigate, verificationAttempt]);

  const login = (userData) => {
    setSessionError('');
    setIsAuthenticated(true);
    setUser(userData);
  };

  const logout = async () => {
    if (localStorage.getItem('authToken')) {
      try { await api.post('/auth/logout'); }
      catch (error) {
        if (![401, 403].includes(error?.response?.status)) {
          toast.error('Could not revoke your session. Please retry signing out.');
          return;
        }
      }
    }
    try { clearQuestionRecovery(); } catch { /* Storage denial must not prevent sign-out. */ }
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('isFirstTimeLogin');
    localStorage.removeItem('activeRole');
    setIsAuthenticated(false);
    setUser(null);
    setSessionError('');
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    sessionError,
    retrySession: () => { setIsLoading(true); setVerificationAttempt(value => value + 1); },
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
